<template>
  <b-modal
    modal-class="modal-carrusel"
    v-model="mostrarModal"
    :title="titulo || 'Previsualización del estudio'"
    size="lg"
    centered
    hide-footer
    header-bg-variant="primary"
    header-text-variant="white"
  >
    <div v-if="urls.length" class="carrusel-container">
      <!-- Área de imagen con zoom -->
      <div
        class="image-container"
        @mousedown="iniciarArrastre"
        @mousemove="arrastrar"
        @mouseup="detenerArrastre"
        @mouseleave="detenerArrastre"
        @wheel.prevent="handleWheel"
        :style="{ cursor: nivelZoom > 1 ? (estaArrastrando ? 'grabbing' : 'grab') : 'default' }"
      >
        <img
          :src="urls[indiceActual]"
          class="carousel-image"
          alt="Preview análisis"
          @error="onImageError"
          :style="{
            transform: `scale(${nivelZoom}) translate(${posicionZoomX / nivelZoom}px, ${posicionZoomY / nivelZoom}px)`,
            transition: estaArrastrando ? 'none' : 'transform 0.2s ease'
          }"
          draggable="false"
        />
      </div>

      <p
        v-if="datosImagenes[indiceActual]"
        class="text-center m-0"
        style="font-size: 13px; color: #555;"
      >
        Estudio: {{ datosImagenes[indiceActual].analysis_type }}
        &ndash;
        Fecha de creación: {{ datosImagenes[indiceActual].created_at | formatoFecha }}
      </p>

      <div class="controles-carrusel">
        <!-- Navegación -->
        <div class="nav-controles">
          <button
            class="nav-btn"
            :disabled="urls.length <= 1"
            @click="irAnterior"
            title="Imagen anterior"
          >
            <b-icon icon="chevron-left" font-scale="1.2"></b-icon>
          </button>

          <span class="contador-imagen">
            {{ indiceActual + 1 }} / {{ urls.length }}
          </span>

          <button
            class="nav-btn"
            :disabled="urls.length <= 1"
            @click="irSiguiente"
            title="Imagen siguiente"
          >
            <b-icon icon="chevron-right" font-scale="1.2"></b-icon>
          </button>
        </div>

        <!-- Separador -->
        <div class="control-separator"></div>

        <!-- Controles de zoom -->
        <div class="zoom-controles">
          <button class="zoom-ctrl-btn" @click="ajustarZoom(-0.25)" :disabled="nivelZoom <= 1" title="Reducir zoom">
            <b-icon icon="dash" font-scale="1.1"></b-icon>
          </button>

          <div class="zoom-slider-wrap">
            <input
              type="range"
              class="zoom-slider"
              min="1"
              max="4"
              step="0.1"
              :value="nivelZoom"
              @input="setZoom(parseFloat($event.target.value))"
            />
          </div>

          <button class="zoom-ctrl-btn" @click="ajustarZoom(0.25)" :disabled="nivelZoom >= 4" title="Aumentar zoom">
            <b-icon icon="plus" font-scale="1.1"></b-icon>
          </button>

          <span class="zoom-label">{{ Math.round(nivelZoom * 100) }}%</span>

          <button
            class="zoom-ctrl-btn reset-btn"
            @click="resetZoom"
            :disabled="nivelZoom === 1"
            title="Restablecer zoom"
          >
            <b-icon icon="arrow-counterclockwise" font-scale="1"></b-icon>
          </button>
        </div>
      </div>
    </div>

    <div v-else class="text-muted text-center py-5">
      <b-icon icon="image" font-scale="4"></b-icon>
      <p class="mt-3">Este estudio no tiene imágenes de previsualización.</p>
    </div>

    <div class="d-flex justify-content-center mt-3">
      <b-button
        variant="outline-secondary"
        block
        pill
        style="width: 20%"
        @click="cerrarModal"
      >
        Cerrar
      </b-button>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "ImageZoomCarousel",
  props: {
    /**
     * Controla la visibilidad del modal (v-model)
     */
    value: {
      type: Boolean,
      default: false,
    },
    /**
     * Título del modal
     */
    titulo: {
      type: String,
      default: "",
    },
    /**
     * Array de URLs de imágenes a mostrar
     */
    urls: {
      type: Array,
      default: () => [],
    },
    /**
     * Array con datos adicionales de cada imagen (analysis_type, created_at, etc.)
     */
    datosImagenes: {
      type: Array,
      default: () => [],
    },
    /**
     * Índice inicial de la imagen a mostrar
     */
    indiceInicial: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      indiceActual: 0,
      nivelZoom: 1,
      posicionZoomX: 0,
      posicionZoomY: 0,
      estaArrastrando: false,
      inicioArrastreX: 0,
      inicioArrastreY: 0,
    };
  },
  computed: {
    mostrarModal: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        // Al abrir el modal, resetear valores
        this.indiceActual = this.indiceInicial;
        this.resetZoom();
      }
    },
  },
  methods: {
    cerrarModal() {
      this.mostrarModal = false;
    },

    irAnterior() {
      this.indiceActual = (this.indiceActual - 1 + this.urls.length) % this.urls.length;
      this.resetZoom();
    },

    irSiguiente() {
      this.indiceActual = (this.indiceActual + 1) % this.urls.length;
      this.resetZoom();
    },

    ajustarZoom(delta) {
      const nuevo = Math.max(1, Math.min(4, this.nivelZoom + delta));
      this.setZoom(nuevo);
    },

    setZoom(valor) {
      this.nivelZoom = parseFloat(parseFloat(valor).toFixed(2));
      if (this.nivelZoom === 1) {
        this.posicionZoomX = 0;
        this.posicionZoomY = 0;
      }
    },

    resetZoom() {
      this.nivelZoom = 1;
      this.posicionZoomX = 0;
      this.posicionZoomY = 0;
    },

    handleWheel(event) {
      const delta = event.deltaY > 0 ? -0.15 : 0.15;
      const nuevoZoom = Math.max(1, Math.min(4, this.nivelZoom + delta));

      if (nuevoZoom !== this.nivelZoom) {
        const rect = event.currentTarget.getBoundingClientRect();
        const mouseX = event.clientX - rect.left - rect.width / 2;
        const mouseY = event.clientY - rect.top - rect.height / 2;

        const escala = nuevoZoom / this.nivelZoom;
        this.posicionZoomX = mouseX + (this.posicionZoomX - mouseX) * escala;
        this.posicionZoomY = mouseY + (this.posicionZoomY - mouseY) * escala;

        this.nivelZoom = parseFloat(nuevoZoom.toFixed(2));

        if (this.nivelZoom === 1) {
          this.posicionZoomX = 0;
          this.posicionZoomY = 0;
        }
      }
    },

    iniciarArrastre(event) {
      if (this.nivelZoom <= 1) return;
      this.estaArrastrando = true;
      this.inicioArrastreX = event.clientX - this.posicionZoomX;
      this.inicioArrastreY = event.clientY - this.posicionZoomY;
    },

    arrastrar(event) {
      if (!this.estaArrastrando || this.nivelZoom <= 1) return;
      event.preventDefault();
      this.posicionZoomX = event.clientX - this.inicioArrastreX;
      this.posicionZoomY = event.clientY - this.inicioArrastreY;
    },

    detenerArrastre() {
      this.estaArrastrando = false;
    },

    onImageError(event) {
      event.target.src = require("../../assets/version5/diagnocat.png");
    },
  },
};
</script>

<style scoped>
.carrusel-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Contenedor de la imagen */
.image-container {
  position: relative;
  width: 100%;
  height: 60vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  user-select: none;
}

.carousel-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transform-origin: center center;
}

/* Barra de controles inferior */
.controles-carrusel {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: #f4f6f8;
  border-radius: 12px;
  padding: 10px 16px;
}

.nav-controles {
  display: flex;
  align-items: center;
  gap: 10px;
}

.nav-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: #1b81af;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, transform 0.15s;
}

.nav-btn:hover:not(:disabled) {
  background-color: #156a90;
  transform: scale(1.08);
}

.nav-btn:disabled {
  background-color: #ccc;
  cursor: default;
}

.contador-imagen {
  font-size: 14px;
  font-weight: 600;
  color: #555;
  min-width: 50px;
  text-align: center;
}

.control-separator {
  width: 1px;
  height: 28px;
  background-color: #d0d5dd;
}

/* Controles de zoom */
.zoom-controles {
  display: flex;
  align-items: center;
  gap: 8px;
}

.zoom-ctrl-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #ccc;
  background: white;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, border-color 0.2s;
}

.zoom-ctrl-btn:hover:not(:disabled) {
  background-color: #e8f4fd;
  border-color: #1b81af;
  color: #1b81af;
}

.zoom-ctrl-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.reset-btn {
  color: #888;
}

.zoom-slider-wrap {
  width: 100px;
}

.zoom-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 4px;
  background: #d0d5dd;
  outline: none;
  cursor: pointer;
}

.zoom-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #1b81af;
  cursor: pointer;
  transition: background 0.2s;
}

.zoom-slider::-webkit-slider-thumb:hover {
  background: #156a90;
}

.zoom-label {
  font-size: 12px;
  font-weight: 600;
  color: #555;
  min-width: 40px;
  text-align: center;
}
</style>
